
import { getFacebookProfile, loginWithFacebook } from '@/api/auth'
import { ILoginResponse, IFacebookLoginRequest } from '@/interfaces/auth'
import { fbTrack } from '@/utils'
import store from '@/store'

const getRandomPassword = () => {
  const generatePassword = (
    length = 16,
    wishlist = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!#$%&*+,-.:;<>?@^_{|}~'
  ) => Array(length)
    .fill('')
    .map(() => wishlist[Math.floor(crypto.getRandomValues(new Uint32Array(1))[0] / (0xffffffff + 1) * wishlist.length)])
    .join('')

  return generatePassword()
}

export default class Facebook {
  static FB: any;

  static getDialogOAuthUrl (state: string) : string {
    return `https://www.facebook.com/${process.env.VUE_APP_FB_JSSDK_VERSION}/dialog/oauth?client_id=${process.env.VUE_APP_FB_APP_ID}&redirect_uri=${process.env.VUE_APP_HOST}${process.env.VUE_APP_PATH}/login&state=${state}&scope=email,public_profile&response_type=code%20granted_scopes`
  }

  static async loginWithGraphApi (accessToken: string): Promise<ILoginResponse | undefined> {
    try {
      const params = {
        fields: 'id,name,email',
        access_token: accessToken
      }
      const response = await getFacebookProfile(params)
      const profile = response.data
      const requestBody = {
        input_token: accessToken,
        password: getRandomPassword(),
        // authResponse.userID is equal to profile.id
        account: profile.id,
        email: profile.email || '',
        nickname: profile.name || profile.short_name || 'FB User',
        // For checking if the user is login with Facebook, we add a field called id.
        id: profile.id
      }
      store.commit('logger/PUSH_LOG', profile)

      // Invoke nuPhoto API to login.
      const { data } = await loginWithFacebook(requestBody)
      if (data.flag === 0) {
        store.commit('SET_TOKEN', data)
        store.commit('SET_USER_INFO', { ...requestBody, expireTime: data.expire_time })
        store.commit('logger/PUSH_LOG', `Facebook login: ${JSON.stringify(data)}`)

        // Facebook Pixel
        fbTrack('CompleteRegistration', { value: 0, currency: 'USD' })
        return Promise.resolve(data)
      } else {
        store.commit('RESET')
        return Promise.reject(new Error(data.msg))
      }
    } catch (error) {
      console.error(error)
      return Promise.reject(error)
    }
  }

  static async initializeApp () : Promise<void> {
    if (!Facebook.FB) {
      console.log('Load Facebook SDK')
      window.fbAsyncInit = async function () {
        window.FB.init({
          appId: process.env.VUE_APP_FB_APP_ID,
          cookie: true,
          xfbml: true,
          version: process.env.VUE_APP_FB_JSSDK_VERSION,
          status: true
        })
        Facebook.FB = window.FB
        console.log('Facebook SDK Loaded')
        store.commit('logger/PUSH_LOG', 'Facebook SDK Loaded')
        const response = await Facebook.getLoginStatus()
        store.commit('logger/PUSH_LOG', response)
        if (response.status === 'connected') {
          console.log('connected')
          Facebook.getProfile()
        }
      }
    }
  }

  static isFacebookInit (): any {
    return Facebook.FB
  }

  // Facebook login
  static async login () : Promise<ILoginResponse | undefined> {
    // 2021/05/04 OBSOLETE, see getDialogOAuthUrl and loginWithGraphApi above
    // Reference: https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus
    // Check login status
    const response: fb.StatusResponse = await Facebook.getLoginStatus()
    if (response.status === 'connected') {
      // Already logged in, not need to login again.
      Facebook.logout()
      return Promise.reject(new Error('您先前已經登入，系統已將您登出，請稍後再試一次。'))
    } else {
      // Prepare nuPhoto login data.
      const facebookLoginRequestBody = await new Promise<IFacebookLoginRequest>((resolve, reject) => {
        store.commit('logger/PUSH_LOG', 'Facebook login')
        // Login
        Facebook.FB.login(async (loginResponse: fb.StatusResponse) => {
          const authResponse: fb.AuthResponse = loginResponse.authResponse
          store.commit('logger/PUSH_LOG', authResponse)
          // Get user's profile
          const profile: any = await Facebook.getProfile() || {}

          if (loginResponse.authResponse) {
            const data = {
              input_token: authResponse.accessToken,
              password: getRandomPassword(),
              // authResponse.userID is equal to profile.id
              account: authResponse.userID,
              email: profile.email || '',
              nickname: profile.name || profile.short_name || 'FB User',
              // For checking if the user is login with Facebook, we add a field called id.
              id: profile.id
            }
            store.commit('logger/PUSH_LOG', data)
            resolve(data)
          } else {
            reject(new Error('登入失敗 (Code: 502)'))
          }
        }, { scope: 'email, public_profile' })
      })

      try {
        // Invoke nuPhoto API to login.
        const { data } = await loginWithFacebook(facebookLoginRequestBody)
        if (data.flag === 0) {
          store.commit('SET_TOKEN', data)
          store.commit('SET_USER_INFO', { ...facebookLoginRequestBody, expireTime: data.expire_time })
          store.commit('logger/PUSH_LOG', `Facebook login: ${JSON.stringify(data)}`)
          return Promise.resolve(data)
        } else {
          store.commit('RESET')
          return Promise.reject(new Error(data.msg))
        }
      } catch (error) {
        console.error(error)
        return Promise.reject(error)
      }
    }
  }

  static logout (): void {
    Facebook.FB.getLoginStatus(function (response: fb.StatusResponse) {
      // Check login status
      if (response.status === 'connected') {
        // Remove permissions
        Facebook.FB.api('/me/permissions', 'DELETE', () => {
          // Logout
          Facebook.FB.logout(() => {
            store.commit('RESET')
          })
        })
      } else {
        store.commit('RESET')
      }
    })
  }

  static getLoginStatus () : Promise<fb.StatusResponse> {
    return new Promise<fb.StatusResponse>((resolve) => {
      Facebook.FB.getLoginStatus((response: fb.StatusResponse) => {
        resolve(response)
      })
    })
  }

  static getProfile () : Promise<any> {
    return new Promise<fb.DialogResponse>((resolve) => {
      Facebook.FB.api('/me?fields=name,id,email', (response: any) => {
        resolve(response)
      })
    })
  }
}
